import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscribeAuth } from "../services/auth";
import { submitListing } from "../services/sellers";
import { getDeviceFingerprint } from "../services/fingerprint";
import { GAMES, REGIONS, SEGMENTS, LANGUAGES,
  PAYMENT_METHODS } from "../services/constants";
import { normalizeHandle, handleValidationError } from "../utils/handles";

// Phone-number validation (E.164 style): leading +, country code
// starts with 1-9, 8-15 digits total. Users can enter a plain
// 10-digit local number too — we prefix +91 for those so India-first
// UX doesn't force people to know E.164 by heart.
const PHONE_RE_E164 = /^\+[1-9]\d{7,14}$/;
function normalizePhone(raw) {
  const trimmed = String(raw || "").replace(/[\s\-()]/g, "");
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  if (/^\d{10}$/.test(trimmed)) return `+91${trimmed}`;
  return trimmed;
}
function phoneValidationError(raw) {
  const n = normalizePhone(raw);
  if (!n) return "Phone number is required.";
  if (!PHONE_RE_E164.test(n)) return "Enter a valid phone number with country code (e.g. +919876543210).";
  return null;
}

// Step-by-step listing creator. Server (popMarketSubmitListing) validates
// every field again — this UI is a happy-path helper, not the trust layer.
const STEPS = ["Identity", "Profile", "Stock & rates", "Contact"];

export default function Post() {
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    handle: "",
    game: "BGMI",
    ign: "",
    gameAccountId: "",
    displayName: "",
    region: "India",
    countryCode: "IN",
    languages: ["en"],
    currency: "INR",
    bio: "",
    stock: { mix: 0, lumen: 0, helicopter: 0, premium: 0 },
    rates: {
      mix:        { base: "", surge: "" },
      lumen:      { base: "", surge: "" },
      helicopter: { base: "", surge: "" },
      premium:    { base: "", surge: "" },
    },
    // Single phone-number channel; treated as both WhatsApp and
    // phone at write time so the reveal-contact flow can offer
    // either without asking a second time.
    contact: { phone: "" },
    paymentMethods: [],
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => subscribeAuth(setUser), []);

  // Prefill phone from what the user entered at sign-in. Saved by Auth.jsx
  // under `popmarket-profile-phone`.
  useEffect(() => {
    const stashed = localStorage.getItem("popmarket-profile-phone");
    if (stashed) {
      setForm((f) => f.contact.phone ? f : { ...f, contact: { ...f.contact, phone: stashed } });
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (k1, k2, v) => setForm((f) => ({
    ...f, [k1]: { ...(f[k1] || {}), [k2]: v },
  }));

  // Anonymous session users can browse the marketplace but must
  // sign in with a real (email / phone / provider) account before
  // creating a storefront — anonymous UIDs rotate on every reinstall
  // and the callable rejects them anyway.
  if (!user || user.isAnonymous) {
    return (
      <div className="container" style={{ maxWidth: 460, paddingTop: 40, textAlign: "center" }}>
        <p>Sign in with your email to create a storefront.</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <a href={`${import.meta.env.BASE_URL}signin`} className="auth-btn primary">Sign in</a>
          <a href={`${import.meta.env.BASE_URL}signup`} className="auth-btn ghost">Create an account</a>
        </div>
      </div>
    );
  }

  const stepValid = () => {
    if (step === 0) {
      if (handleValidationError(form.handle)) return false;
      if (!form.game) return false;
      if (!form.ign || !form.gameAccountId) return false;
      return true;
    }
    if (step === 1) {
      if (!form.displayName) return false;
      if (!form.region) return false;
      if (form.languages.length === 0) return false;
      if (!form.currency) return false;
      return true;
    }
    if (step === 2) {
      // Need at least one segment with positive stock OR a rate set.
      return SEGMENTS.some((s) =>
          Number(form.stock[s.key]) > 0 || Number(form.rates[s.key].base) > 0);
    }
    if (step === 3) {
      // Phone number is the only contact channel now — and it must
      // parse as a valid E.164-style number after normalisation.
      return !phoneValidationError(form.contact.phone);
    }
    return true;
  };

  const next = () => setStep(Math.min(step + 1, STEPS.length - 1));
  const back = () => setStep(Math.max(step - 1, 0));

  const onSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const phone = normalizePhone(form.contact.phone);
      const phoneErr = phoneValidationError(phone);
      if (phoneErr) {
        setError(phoneErr);
        setSubmitting(false);
        return;
      }
      const deviceFingerprint = await getDeviceFingerprint();
      const payload = {
        ...form,
        handle: normalizeHandle(form.handle),
        deviceFingerprint,
        // Same number is exposed as BOTH a phone contact and a
        // WhatsApp contact — most sellers use one number for both,
        // and asking a second time was the top drop-off point.
        contact: { phone, whatsapp: phone },
        // Numerify rate strings.
        rates: Object.fromEntries(SEGMENTS.map((s) => [s.key, {
          base:  numOrNull(form.rates[s.key].base),
          surge: numOrNull(form.rates[s.key].surge),
        }])),
      };
      const res = await submitListing(payload);
      // The submit callable always writes to the live `pop_market_sellers`
      // collection, but the client-side reader defaults to the seed
      // collection until `?live=1` flips localStorage. Force that flip
      // here so the freshly-created storefront is actually visible.
      if (res?.handle) navigate(`/seller/${res.handle}?live=1`);
      else navigate("/me?live=1");
    } catch (e) {
      setError(e.message || "Couldn't submit. Try again, or contact PA support if this persists.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container pmp-container" style={{ maxWidth: 640, paddingBottom: 80 }}>
      <style>{`
        .pmp-container input,
        .pmp-container select,
        .pmp-container textarea {
          width: 100%;
          box-sizing: border-box;
          min-width: 0;
        }
        @media (max-width: 560px) {
          .pmp-container { padding-left: 12px; padding-right: 12px; }
          .pmp-container h1 { font-size: 20px !important; }
          .pmp-rate-row {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          }
          .pmp-rate-row > :first-child {
            grid-column: 1 / -1;
          }
          .pmp-nav-row {
            gap: 8px;
          }
          .pmp-nav-row > * {
            flex: 1 1 0;
          }
        }
      `}</style>
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: "8px 0 8px" }}>Create your storefront</h1>
      <p className="muted" style={{ fontSize: 13 }}>
        Step {step + 1} of {STEPS.length} — {STEPS[step]}
      </p>
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {STEPS.map((_, i) => (
          <div key={i} style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: i <= step ? "var(--accent)" : "var(--bg-elev-2)",
          }} />
        ))}
      </div>

      {error && (
        <div style={{ background: "rgba(232,90,45,0.12)", border: "1px solid rgba(232,90,45,0.35)",
          color: "var(--danger)", padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {step === 0 && <Step0 form={form} set={set} />}
      {step === 1 && <Step1 form={form} set={set} setNested={setNested} />}
      {step === 2 && <Step2 form={form} set={set} setNested={setNested} />}
      {step === 3 && <Step3 form={form} set={set} setNested={setNested} />}

      <div className="pmp-nav-row" style={{ display: "flex", justifyContent: "space-between", marginTop: 24, gap: 12 }}>
        <button onClick={back} disabled={step === 0}>Back</button>
        {step < STEPS.length - 1 ? (
          <button className="primary" disabled={!stepValid()} onClick={next}>
            Next
          </button>
        ) : (
          <button className="primary" disabled={!stepValid() || submitting} onClick={onSubmit}>
            {submitting ? "Creating…" : "Create storefront"}
          </button>
        )}
      </div>
    </div>
  );
}

function numOrNull(x) {
  const n = Number(x);
  return isFinite(n) && x !== "" ? n : null;
}

// ── Steps ────────────────────────────────────────────────────────────────────

function Step0({ form, set }) {
  const handleErr = form.handle && handleValidationError(form.handle);
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="HANDLE (your @ on Pop Market)">
        <input
          type="text"
          value={form.handle}
          onChange={(e) => set("handle", normalizeHandle(e.target.value))}
          placeholder="rapidgorilla1"
        />
        {handleErr && <div className="dim" style={{ fontSize: 11, color: "var(--danger)" }}>{handleErr}</div>}
      </Field>
      <Field label="GAME">
        <select value={form.game} onChange={(e) => set("game", e.target.value)}>
          {GAMES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
          One storefront = one game. If you sell on both, create a second
          PlayAware account for the other game.
        </div>
      </Field>
      <Field label={`${form.game} IN-GAME NAME`}>
        <input type="text" value={form.ign}
          onChange={(e) => set("ign", e.target.value)}
          placeholder="RapidGorilla1" />
      </Field>
      <Field label={`${form.game} CHARACTER ID`}>
        <input type="text" inputMode="numeric" value={form.gameAccountId}
          onChange={(e) => set("gameAccountId", e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="5234567890" />
        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
          The numeric character ID from your in-game profile. We'll verify
          it before activating your storefront.
        </div>
      </Field>
    </div>
  );
}

function Step1({ form, set }) {
  const toggleLang = (code) => {
    const cur = new Set(form.languages);
    cur.has(code) ? cur.delete(code) : cur.add(code);
    set("languages", Array.from(cur));
  };
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="DISPLAY NAME">
        <input value={form.displayName}
          onChange={(e) => set("displayName", e.target.value)} />
      </Field>
      <Field label="REGION">
        <select value={form.region} onChange={(e) => set("region", e.target.value)}>
          {REGIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </Field>
      <Field label="COUNTRY CODE (ISO-2)">
        <input value={form.countryCode}
          onChange={(e) => set("countryCode", e.target.value.toUpperCase().slice(0, 2))}
          placeholder="IN" maxLength={2} />
      </Field>
      <Field label="LANGUAGES YOU OPERATE IN">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => toggleLang(l.code)}
              style={{
                background: form.languages.includes(l.code) ? "var(--accent)" : "var(--bg-elev-2)",
                color: form.languages.includes(l.code) ? "#1a0f00" : "var(--text)",
                fontWeight: 700,
                fontSize: 12,
                padding: "5px 10px",
              }}
            >{l.label}</button>
          ))}
        </div>
      </Field>
      <Field label="QUOTED CURRENCY">
        <select value={form.currency} onChange={(e) => set("currency", e.target.value)}>
          {["INR","USD","EUR","AED","IDR","PHP","BRL","PKR","BDT","CNY","JPY"].map((c) =>
            <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="BIO (optional)">
        <textarea rows={3} maxLength={500} value={form.bio}
          onChange={(e) => set("bio", e.target.value)}
          placeholder="2 lines about your shop — years operating, specialty segments, response time, etc." />
      </Field>
    </div>
  );
}

function Step2({ form, set, setNested }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Set stock + rates for any segments you offer. Leave others at 0.
        Mix &amp; Premium are quoted per 1,000 popularity. Lumen &amp;
        Helicopter are quoted per item.
      </p>
      {SEGMENTS.map((s) => {
        const isFlat = s.key === "lumen" || s.key === "helicopter";
        return (
          <div key={s.key} className="card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span className={`segment-pill ${s.cssClass}`}>{s.label}</span>
              <span className="dim" style={{ fontSize: 12 }}>{s.blurb}</span>
            </div>
            <div className="pmp-rate-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <Field label="STOCK">
                <input type="number" inputMode="numeric"
                  value={form.stock[s.key] || ""}
                  onChange={(e) => set("stock", { ...form.stock, [s.key]: Number(e.target.value) || 0 })} />
              </Field>
              <Field label={`BASE RATE (${form.currency}${isFlat ? "" : "/k"})`}>
                <input type="number" step="0.01" inputMode="decimal"
                  value={form.rates[s.key].base}
                  onChange={(e) => setNested("rates", s.key, { ...form.rates[s.key], base: e.target.value })} />
              </Field>
              <Field label={`SURGE RATE (${form.currency}${isFlat ? "" : "/k"})`}>
                <input type="number" step="0.01" inputMode="decimal"
                  value={form.rates[s.key].surge}
                  onChange={(e) => setNested("rates", s.key, { ...form.rates[s.key], surge: e.target.value })} />
              </Field>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Step3({ form, set, setNested }) {
  const togglePay = (m) => {
    const cur = new Set(form.paymentMethods);
    cur.has(m) ? cur.delete(m) : cur.add(m);
    set("paymentMethods", Array.from(cur));
  };
  const phoneErr = form.contact.phone
      ? phoneValidationError(form.contact.phone)
      : null;
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="📞 PHONE / WHATSAPP NUMBER">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={form.contact.phone}
          onChange={(e) => setNested("contact", "phone", e.target.value)}
          placeholder="+919876543210 or 9876543210"
        />
        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
          The same number is exposed to buyers as both your phone
          and your WhatsApp. 10-digit Indian numbers are auto-prefixed
          with +91.
        </div>
        {phoneErr && (
          <div className="dim" style={{ fontSize: 11, color: "var(--danger)" }}>
            {phoneErr}
          </div>
        )}
      </Field>
      <Field label="PAYMENT METHODS YOU ACCEPT">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {PAYMENT_METHODS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => togglePay(m)}
              style={{
                background: form.paymentMethods.includes(m) ? "var(--accent)" : "var(--bg-elev-2)",
                color: form.paymentMethods.includes(m) ? "#1a0f00" : "var(--text)",
                fontWeight: 700,
                fontSize: 11,
                padding: "5px 10px",
              }}
            >{m}</button>
          ))}
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
          Display-only — buyers see which methods you accept and DM you. PA
          never processes payments.
        </div>
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "var(--text-muted)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}
